import { type HTMLAttributes, type ReactNode, forwardRef } from 'react';

/**
 * The prototype's job board as composable parts (Story #28000, `#board-tax`): the sideways-scrolling board
 * (`.kanban`), its columns (`.kcol` with the coloured header `.kh`, the body `.kb`, the count · $ footer `.kf` and the
 * collapsed `.rail`), the job card (`.jc` with `.ref`, `.nm`, `.ty`, `.mt`, `.chips`, `.acts`) and the card's small
 * chips (`.mini`, `.due`, `.urg`, `.roll`). Styled by primitives.css on the tokens.css values; the column header colour
 * comes from the column's `stage` (one token per stage). Columns and cards forward their ref, so a screen can make them
 * drop targets and draggables.
 */

/** `.kanban` — the board: columns side by side, scrolling sideways in its own frame; stacked below 768px. */
export function Kanban({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <div className="ui-kanban" role={label ? 'region' : undefined} aria-label={label}>
      {children}
    </div>
  );
}

export interface KanbanColumnProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** The stage key (e.g. IN_PROGRESS); picks the header colour. */
  stage?: string;
  /** The column name, drawn in the header as the column's heading. */
  title: string;
  count: number;
  /** data-testid for the header count. */
  countTestId?: string;
  /** `.kf` — e.g. "3 · $8,180". Omitted on the collapsed rail. */
  footer?: ReactNode;
  /** A drag is over the column. */
  over?: boolean;
  /** Collapsible column (the prototype's Completed rail): the header toggles it. */
  collapsed?: boolean;
  onToggle?: () => void;
  children?: ReactNode;
}

/** `.kcol` — one board column. Collapsed, it is the prototype's narrow rail (`.kcol.rail`), still a drop target. */
export const KanbanColumn = forwardRef<HTMLDivElement, KanbanColumnProps>(function KanbanColumn(
  { stage, title, count, countTestId, footer, over = false, collapsed = false, onToggle, children, className = '', ...rest },
  ref,
) {
  const classes = ['ui-kcol', collapsed ? 'ui-rail' : '', over ? 'ui-over' : '', className].filter(Boolean).join(' ');
  if (collapsed) {
    return (
      <div ref={ref} className={classes} data-stage={stage} {...rest}>
        <button type="button" className="ui-kh" aria-label={`Expand ${title} column`} onClick={onToggle}>
          <span>
            <span>{title}</span> <span aria-hidden="true">▸</span>
          </span>
          <span data-testid={countTestId}>{count}</span>
        </button>
      </div>
    );
  }
  return (
    <div ref={ref} className={classes} data-stage={stage} {...rest}>
      <div className="ui-kh">
        <h2>{title}</h2>
        <span className="ui-kh-r">
          <span data-testid={countTestId}>{count}</span>
          {onToggle && (
            <button type="button" className="ui-kh-tog" aria-label={`Collapse ${title} column`} onClick={onToggle}>
              <span aria-hidden="true">◂</span>
            </button>
          )}
        </span>
      </div>
      <div className="ui-kb">{children}</div>
      {footer !== undefined && <div className="ui-kf">{footer}</div>}
    </div>
  );
});

/** `.drop` — the dashed "Drop here" placeholder in an empty column. */
export function DropHint({ children = 'Drop here' }: { children?: ReactNode }) {
  return <div className="ui-drop">{children}</div>;
}

/** `.grp` — a group label inside a column (e.g. BAS / IAS by month, then quarter). */
export function KanbanGroupLabel({ children }: { children: ReactNode }) {
  return <p className="ui-grp">{children}</p>;
}

export type JobCardEdge = 'urgent' | 'risk-o' | 'risk-r';
export type JobCardQuery = 'wait' | 'over';

export interface JobCardProps extends HTMLAttributes<HTMLDivElement> {
  /** `.ref` — the bold navy client ref line, with its tags (`.urg`, `.roll`). */
  refLine: ReactNode;
  /** `.nm` — the client name. */
  name: ReactNode;
  /** `.ty` — job type · period. */
  type?: ReactNode;
  /** Left edge: urgent (red, tinted), orange or red risk. */
  edge?: JobCardEdge;
  /** The whole-card 2px query border on an Out — client card. */
  query?: JobCardQuery;
  /** A parent job: the stacked-card shadow (`.jc.parent`). */
  parent?: boolean;
  /** A Not in phantom card (`.jc.phantom`): dashed, display only. */
  phantom?: boolean;
  /** A completed job, shown faded. */
  done?: boolean;
  children?: ReactNode;
}

/** `.jc` — a job card on the board. The rest of the card (`.mt` lines, `.chips`, `.acts`) is passed as children. */
export const JobCard = forwardRef<HTMLDivElement, JobCardProps>(function JobCard(
  { refLine, name, type, edge, query, parent = false, phantom = false, done = false, children, className = '', ...rest },
  ref,
) {
  const classes = [
    'ui-jc',
    edge ? `ui-${edge}` : '',
    query ? `ui-q-${query}` : '',
    parent ? 'ui-parent' : '',
    phantom ? 'ui-phantom' : '',
    done ? 'ui-done' : '',
    className,
  ].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={classes} {...rest}>
      <div className="ui-jc-ref">{refLine}</div>
      <div className="ui-jc-nm">{name}</div>
      {type !== undefined && type !== null && type !== '' && <div className="ui-jc-ty">{type}</div>}
      {children}
    </div>
  );
});

/** `.jc .mt` — a muted line on the card (people · fee · hours, the dates, a status). */
export function JobCardLine({ children, testId }: { children: ReactNode; testId?: string }) {
  return <div className="ui-jc-mt" data-testid={testId}>{children}</div>;
}

/** `.jc .chips` — the card's row of small chips and badges. */
export function JobCardChips({ children }: { children: ReactNode }) {
  return <div className="ui-jc-chips">{children}</div>;
}

/** `.jc .acts` — the card's row of extra-small buttons. */
export function JobCardActions({ children }: { children: ReactNode }) {
  return <div className="ui-jc-acts">{children}</div>;
}

/** `.mini` — a small grey chip (e.g. "6 / 9", "WIP $2,980 · 88%", "6 d in bucket"). */
export function Mini({ children, testId }: { children: ReactNode; testId?: string }) {
  return <span className="ui-mini" data-testid={testId}>{children}</span>;
}

/** `.urg` — the red "Urgent" tag on the ref line. */
export function UrgentTag({ children = 'Urgent' }: { children?: ReactNode }) {
  return <span className="ui-urg">{children}</span>;
}

/** `.roll` — a parent's roll-up tag on the ref line (e.g. "group · 4"). */
export function RollTag({ children, label }: { children: ReactNode; label?: string }) {
  return <span className="ui-roll" aria-label={label} role={label ? 'img' : undefined}>{children}</span>;
}

export type DueTone = 'g' | 'o' | 'r' | 'none';

/** `.due` — the ATO due date chip: green, orange when soon, red when overdue, amber when not set. */
export function DuePill({ tone, children, testId }: { tone: DueTone; children: ReactNode; testId?: string }) {
  return <span className={`ui-due ui-${tone}`} data-testid={testId}>{children}</span>;
}

/** `.dot` — a small status dot; always paired with words or an accessible name. */
export function StatusDot({ tone, label, title }: { tone?: 'warn'; label: string; title?: string }) {
  return <span className={tone ? `ui-dot ui-${tone}` : 'ui-dot'} role="img" aria-label={label} title={title} />;
}
